import { useEffect, useRef } from 'react'
import { uploadMeetingAudioPart } from '@/services/meetings'

/**
 * Cadence agreed for BRAI-457: at most this much audio is lost with the tab.
 *
 * Also the floor worth asking for. Chrome throttles a backgrounded tab's timers
 * to roughly once a minute, and the tab running the meeting is backgrounded
 * most of the time — the user is watching the tab being shared. A shorter
 * cadence would not survive that; this one lands on the limit rather than
 * pretending to beat it.
 */
export const AUDIO_PART_INTERVAL_MS = 60_000

/**
 * When the first part goes out, ahead of the regular cadence.
 *
 * A meeting that died inside its first minute used to lose everything and
 * leave no trace: no part reached the server, so `lastAudioPartAt` was never
 * written and the recovery sweep — which looks for meetings by that column —
 * could not even see it. Sending one part early buys both the audio and the
 * row that makes the meeting visible to the sweep.
 *
 * Five seconds rather than zero: `MediaRecorder` is started with a one-second
 * timeslice, so an immediate tick would usually find nothing to send.
 */
export const AUDIO_FIRST_PART_DELAY_MS = 5_000

/**
 * The most a single part may weigh.
 *
 * Carrying a failed part forward is what keeps a network blip from punching a
 * hole in the recovered recording, but it has to stay under the 25 MB the
 * upload route accepts. A blob that grows past that is refused with 413 on
 * every retry — and since the first part is the one holding the WebM header
 * and so can never be abandoned, nothing would land at all and the meeting
 * would be left with no recovery whatsoever.
 *
 * Past this the part is cut from the end rather than dropped. The head is
 * where chunk 0's header lives — `ArchiveAudioRecorder` says a recording
 * stitched from parts that start after it is unplayable — so the bytes that go
 * are the ones a player can most afford to lose, and what remains is a WebM
 * truncated mid-cluster, which is exactly what a crashed recording looks like.
 */
export const AUDIO_PART_MAX_CARRY_BYTES = 20 * 1024 * 1024

export interface UsePeriodicAudioPartUploadOptions {
  /** Backend id of the meeting being recorded; null before one exists. */
  meetingId: string | null
  /** False while the meeting is not actively recording (paused, ended, not started). */
  enabled: boolean
  /** Audio captured since the previous part — see `ArchiveAudioRecorder.takeUnsentChunks`. */
  takeUnsentChunks: () => Blob | null
  /** Meeting length right now, stored with the part as the recovered file's duration. */
  getDurationSeconds: () => number
  intervalMs?: number
}

/**
 * Ships the meeting's audio to the server every minute while it records,
 * so a tab that dies mid-meeting no longer takes the whole recording with it
 * (BRAI-457).
 *
 * The recording still uploads in full at End Meeting; these parts exist only
 * for the meeting that never gets there, and the server throws them away as
 * soon as the finished file lands. That is why every failure here is swallowed:
 * a part that cannot be sent costs a few recoverable minutes, while surfacing
 * it would interrupt a meeting that is going perfectly well.
 *
 * A part is skipped — without burning its index — when nothing was recorded
 * since the previous one, so a muted or silent stretch does not litter the
 * bucket with empty objects.
 */
export function usePeriodicAudioPartUpload({
  meetingId,
  enabled,
  takeUnsentChunks,
  getDurationSeconds,
  intervalMs = AUDIO_PART_INTERVAL_MS,
}: UsePeriodicAudioPartUploadOptions): void {
  // One sequence object per meeting: the counter, the carried audio, and the
  // two readers that produce a part. The flush on the way out is asynchronous
  // and runs *after* the incoming meeting has rendered, so anything shared
  // between meetings is read by the wrong one — a counter shared this way
  // stored the new recording's first part as index 1, and readers shared this
  // way put the new meeting's audio under the old meeting's key.
  const sequenceRef = useRef<PartSequence | null>(null)

  // Refreshed during render, but only onto the sequence it belongs to. React
  // renders the incoming meeting before cleaning up the outgoing one's effect,
  // so an unconditional assignment here is exactly how the outgoing flush got
  // hold of the incoming meeting's recorder.
  if (sequenceRef.current?.meetingId === meetingId) {
    sequenceRef.current.takeUnsentChunks = takeUnsentChunks
    sequenceRef.current.getDurationSeconds = getDurationSeconds
  }

  // Read only when a sequence is created, and held in a ref so the effect does
  // not list the readers as dependencies: their identity changes on every
  // parent render, and depending on them would tear down and restart the
  // cadence each time — losing the part that was mid-flight with it.
  const latestReadersRef = useRef({ takeUnsentChunks, getDurationSeconds })
  latestReadersRef.current = { takeUnsentChunks, getDurationSeconds }

  useEffect(() => {
    if (!enabled || !meetingId) return undefined

    // Reused across an enabled/disabled toggle of the same meeting, so pausing
    // and resuming continues the numbering rather than restarting it.
    if (sequenceRef.current?.meetingId !== meetingId) {
      sequenceRef.current = {
        meetingId,
        nextIndex: 0,
        carried: null,
        inFlight: false,
        ...latestReadersRef.current,
      }
    }
    const sequence = sequenceRef.current

    const tick = async () => {
      if (sequence.inFlight) return

      const fresh = sequence.takeUnsentChunks()
      const joined = joinCarriedPart(sequence.carried, fresh)
      if (!joined || joined.size === 0) return
      const part = boundPartSize(joined)

      // The index only moves once the part is actually stored. Advancing it
      // first — which is what this did — meant a failed upload burned its
      // number and left that minute missing from the stitched recording for
      // good, because the chunks had already been handed out.
      const index = sequence.nextIndex
      sequence.inFlight = true
      try {
        await uploadMeetingAudioPart(
          sequence.meetingId,
          index,
          sequence.getDurationSeconds(),
          part
        )
        sequence.nextIndex = index + 1
        sequence.carried = null
      } catch (error) {
        // Always carried, never dropped: this blob begins at chunk 0 while the
        // index is still 0, and that is the only WebM header there is —
        // abandoning it would leave the sweep stitching headerless clusters
        // and publishing the result as `UPLOADED`, a meeting that reports a
        // recording and plays nothing. It is safe to keep because
        // `boundPartSize` has already held it under the server's limit, so the
        // retry is something the upload route can still accept.
        sequence.carried = part
        console.warn(`Meeting audio part ${index} upload failed; carrying it forward`, error)
      } finally {
        sequence.inFlight = false
      }
    }

    // Two timers, not one: the first part goes out early so a meeting that
    // dies in its opening minute is still recoverable, and the cadence then
    // settles at `intervalMs`.
    const firstTimer = window.setTimeout(() => void tick(), AUDIO_FIRST_PART_DELAY_MS)
    const timer = window.setInterval(() => void tick(), intervalMs)

    return () => {
      window.clearTimeout(firstTimer)
      window.clearInterval(timer)
      // One last part on the way out, so the seconds since the previous tick
      // are not simply dropped when the meeting is paused or the recorder
      // stops. Fire-and-forget: a pause leaves the page up long enough for it
      // to land, and End Meeting uploads the whole file over the top anyway.
      void tick()
    }
  }, [enabled, meetingId, intervalMs])
}

/**
 * The blob to send: whatever a previous part failed to deliver, followed by
 * what has been recorded since.
 *
 * Order matters — the carried audio came first in the recording, and parts are
 * stitched back together in the order they were stored.
 *
 * @returns Null when there is nothing on either side.
 */
interface PartSequence {
  /** The meeting these indexes belong to; never reused by another one. */
  readonly meetingId: string
  nextIndex: number
  /** Audio from a part that failed, waiting to go out in front of the next one. */
  carried: Blob | null
  /** Guards against a slow upload overlapping the next tick and reordering parts. */
  inFlight: boolean
  /**
   * This meeting's own readers, not the latest ones the hook was rendered
   * with. Both are kept here so the outgoing meeting's final part is cut from
   * the recorder that produced it.
   */
  takeUnsentChunks: () => Blob | null
  getDurationSeconds: () => number
}

function joinCarriedPart(carried: Blob | null, fresh: Blob | null): Blob | null {
  if (!carried) return fresh
  if (!fresh || fresh.size === 0) return carried
  return new Blob([carried, fresh], { type: carried.type || fresh.type })
}

/**
 * Holds one part under {@link AUDIO_PART_MAX_CARRY_BYTES} by cutting its tail.
 *
 * Cutting rather than dropping, and the tail rather than the head: the head is
 * where the WebM header is, and a part that loses it takes the whole recovered
 * recording with it. What a long outage costs is therefore the middle of the
 * stretch it covers, not the ability to play any of it.
 */
function boundPartSize(part: Blob): Blob {
  if (part.size <= AUDIO_PART_MAX_CARRY_BYTES) return part
  return part.slice(0, AUDIO_PART_MAX_CARRY_BYTES, part.type)
}
